import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight, Check, QrCode, KeyRound, UserCheck, FileText,
  Database, UserPlus, Users, UserCog, User, UserMinus,
  Lock, Smartphone, Ban, MonitorCheck, ScrollText, PenLine, Settings,
} from "lucide-react";
import LandingNav from "../components/landing/LandingNav";
import Footer from "../components/landing/Footer";
import { LEGAL_EMAIL } from "../lib/legal";

const fadeUp = {
  initial: { opacity: 0, y: 16 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-60px" },
};

const TRUST = ["Aislamiento entre empresas", "Acceso autenticado", "QR privado"];

const ISOLATION_CARDS = [
  {
    icon: Database,
    title: "Datos aislados",
    text: "Máquinas, ubicaciones, documentos, fotografías, intervenciones y usuarios están vinculados a su organización.",
  },
  {
    icon: UserPlus,
    title: "Acceso por usuario",
    text: "Cada persona accede con su propia cuenta. Las credenciales no necesitan compartirse entre trabajadores.",
  },
  {
    icon: Users,
    title: "Roles definidos",
    text: "Los administradores gestionan quién forma parte de su empresa y pueden añadir o eliminar usuarios.",
  },
];

const QR_FLOW = [
  { icon: QrCode, label: "QR" },
  { icon: KeyRound, label: "Inicio de sesión" },
  { icon: UserCheck, label: "Autorización" },
  { icon: FileText, label: "Pasaporte" },
];

const CONTROL_CARDS = [
  {
    icon: UserCog,
    title: "Administradores",
    text: "Gestionan los usuarios de su organización.",
  },
  {
    icon: User,
    title: "Usuarios",
    text: "Trabajan únicamente dentro del entorno de su empresa.",
  },
  {
    icon: UserMinus,
    title: "Accesos revocables",
    text: "Cuando una persona deja de necesitar acceso, el administrador puede eliminar su usuario.",
  },
];

const ACCOUNT_ITEMS = [
  {
    icon: Lock,
    title: "Contraseñas protegidas",
    text: "Las contraseñas no se almacenan como texto legible.",
  },
  {
    icon: Smartphone,
    title: "Autenticación en dos pasos",
    text: "Los administradores pueden reforzar su cuenta mediante autenticación en dos factores (2FA).",
  },
  {
    icon: Ban,
    title: "Protección frente a intentos de acceso",
    text: "El sistema incorpora límites y controles frente a intentos repetidos o abusivos.",
  },
  {
    icon: MonitorCheck,
    title: "Sesiones protegidas",
    text: "La plataforma utiliza mecanismos de autenticación y control de sesión para proteger el acceso.",
  },
];

const TRACE_ITEMS = [
  {
    icon: ScrollText,
    title: "Registro de actividad",
    text: "Las operaciones relevantes generan eventos de auditoría.",
  },
  {
    icon: PenLine,
    title: "Autoría",
    text: "Las intervenciones conservan información sobre quién las registró.",
  },
  {
    icon: Settings,
    title: "Administración",
    text: "Las acciones sensibles de gestión de la plataforma también pueden quedar registradas.",
  },
];

function SectionHead({ title, body, testid }) {
  return (
    <motion.div {...fadeUp} transition={{ duration: 0.55 }} className="max-w-3xl">
      <h2
        className="font-heading text-3xl sm:text-4xl font-bold tracking-tight text-zinc-950 leading-[1.12]"
        data-testid={testid}
      >
        {title}
      </h2>
      {body && <p className="text-lg text-zinc-600 leading-relaxed mt-5">{body}</p>}
    </motion.div>
  );
}

function Card({ icon: Icon, title, text, i, testid }) {
  return (
    <motion.div
      {...fadeUp}
      transition={{ duration: 0.45, delay: (i % 3) * 0.08 }}
      className="bg-white rounded-2xl border border-zinc-200/80 card-shadow p-7"
      data-testid={testid}
    >
      <span className="h-11 w-11 rounded-xl bg-zinc-100 flex items-center justify-center text-zinc-700 mb-5">
        <Icon size={19} strokeWidth={1.8} />
      </span>
      <h3 className="font-heading text-lg font-semibold tracking-tight text-zinc-900">{title}</h3>
      <p className="text-sm text-zinc-600 leading-relaxed mt-2">{text}</p>
    </motion.div>
  );
}

export default function SecurityPage() {
  return (
    <div className="min-h-screen bg-white" data-testid="security-page">
      <LandingNav />
      <main>
        {/* 1 — Hero */}
        <section className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-20 pt-20 lg:pt-24 pb-14">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.55 }}
            className="max-w-3xl"
          >
            <p className="text-xs uppercase tracking-widest font-medium text-zinc-500 mb-5">Seguridad</p>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-zinc-950 leading-[1.05]">
              Privado por diseño.
            </h1>
            <p className="text-lg sm:text-xl text-zinc-600 leading-relaxed mt-6 max-w-2xl">
              La información de cada empresa está aislada y solo es accesible por sus usuarios
              autorizados. Los QR nunca hacen pública una máquina: escanear exige autenticación y
              pertenecer a la empresa correspondiente.
            </p>
            <div className="flex flex-wrap gap-2.5 mt-8" data-testid="security-trust-indicators">
              {TRUST.map((t) => (
                <span
                  key={t}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-zinc-200 bg-zinc-50 text-sm font-medium text-zinc-700"
                >
                  <Check size={14} className="text-zinc-900" />
                  {t}
                </span>
              ))}
            </div>
          </motion.div>
        </section>

        {/* 2 — Aislamiento entre empresas */}
        <section className="bg-zinc-50 border-y border-zinc-100">
          <div className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-20 py-20 sm:py-24">
            <SectionHead
              title="Cada empresa tiene su propio espacio."
              body="La información de una organización está separada de la del resto de empresas. Los controles de acceso se aplican también en el servidor, no solo en la interfaz."
              testid="security-isolation-heading"
            />
            <div className="grid sm:grid-cols-3 gap-5 mt-12">
              {ISOLATION_CARDS.map((c, i) => (
                <Card key={c.title} {...c} i={i} testid={`security-isolation-card-${i}`} />
              ))}
            </div>
          </div>
        </section>

        {/* 3 — Seguridad del QR */}
        <section className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-20 py-20 sm:py-24">
          <SectionHead
            title="Un QR no convierte tus datos en públicos."
            body="El QR identifica la máquina y permite llegar rápidamente a su pasaporte. Si quien lo escanea no ha iniciado sesión y no pertenece a la empresa autorizada, no puede consultar su información."
            testid="security-qr-heading"
          />
          <motion.div
            {...fadeUp}
            transition={{ duration: 0.55, delay: 0.1 }}
            className="mt-12 bg-white rounded-3xl border border-zinc-200/80 card-shadow px-6 py-10 sm:px-10"
            data-testid="security-qr-flow"
          >
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-5">
              {QR_FLOW.map((step, i) => (
                <div key={step.label} className="flex flex-col sm:flex-row items-center gap-3 sm:gap-5">
                  <div className="flex flex-col items-center gap-3 w-36">
                    <span className="h-14 w-14 rounded-2xl bg-zinc-950 text-white flex items-center justify-center">
                      <step.icon size={22} strokeWidth={1.8} />
                    </span>
                    <span className="text-sm font-medium text-zinc-800 text-center">{step.label}</span>
                  </div>
                  {i < QR_FLOW.length - 1 && (
                    <ArrowRight size={18} className="text-zinc-300 rotate-90 sm:rotate-0 my-1 sm:my-0" />
                  )}
                </div>
              ))}
            </div>
          </motion.div>
        </section>

        {/* 4 — Control de la empresa */}
        <section className="bg-zinc-50 border-y border-zinc-100">
          <div className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-20 py-20 sm:py-24">
            <SectionHead
              title="El control sigue siendo de tu empresa."
              body="Asset Passport organiza la información alrededor de la máquina, pero es tu organización quien decide quién puede acceder."
              testid="security-control-heading"
            />
            <div className="grid sm:grid-cols-3 gap-5 mt-12">
              {CONTROL_CARDS.map((c, i) => (
                <Card key={c.title} {...c} i={i} testid={`security-control-card-${i}`} />
              ))}
            </div>
          </div>
        </section>

        {/* 5 — Protección de la cuenta */}
        <section className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-20 py-20 sm:py-24">
          <SectionHead title="Protegemos también el acceso." testid="security-account-heading" />
          <div className="grid sm:grid-cols-2 gap-5 mt-12 max-w-4xl">
            {ACCOUNT_ITEMS.map((c, i) => (
              <Card key={c.title} {...c} i={i} testid={`security-account-item-${i}`} />
            ))}
          </div>
        </section>

        {/* 6 — Trazabilidad */}
        <section className="bg-zinc-50 border-y border-zinc-100">
          <div className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-20 py-20 sm:py-24">
            <SectionHead
              title="Sabemos quién hizo qué."
              body="Las acciones relevantes dentro de Asset Passport pueden quedar registradas para facilitar la trazabilidad y la administración del entorno."
              testid="security-trace-heading"
            />
            <div className="grid sm:grid-cols-3 gap-5 mt-12">
              {TRACE_ITEMS.map((c, i) => (
                <Card key={c.title} {...c} i={i} testid={`security-trace-item-${i}`} />
              ))}
            </div>
          </div>
        </section>

        {/* 7 — Transparencia */}
        <section className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-20 py-20 sm:py-24">
          <motion.div
            {...fadeUp}
            transition={{ duration: 0.55 }}
            className="max-w-4xl mx-auto bg-zinc-100/70 rounded-3xl px-7 py-12 sm:px-14 text-center"
            data-testid="security-transparency"
          >
            <h2 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight text-zinc-950 leading-tight">
              Seguridad también significa ser transparentes.
            </h2>
            <p className="text-base sm:text-lg text-zinc-600 leading-relaxed mt-5 max-w-2xl mx-auto">
              Asset Passport está en fase piloto. Seguimos reforzando la infraestructura y los
              procesos a medida que incorporamos empresas.
            </p>
            <p className="text-sm text-zinc-500 leading-relaxed mt-4 max-w-2xl mx-auto">
              Si necesitas información sobre seguridad, privacidad o tratamiento de datos antes de
              probar Asset Passport,{" "}
              {LEGAL_EMAIL ? (
                <a
                  href={`mailto:${LEGAL_EMAIL}`}
                  className="underline underline-offset-2 hover:text-zinc-800 transition-colors"
                  data-testid="security-contact-link"
                >
                  contacta con nosotros
                </a>
              ) : (
                <Link
                  to="/solicitar-acceso"
                  className="underline underline-offset-2 hover:text-zinc-800 transition-colors"
                  data-testid="security-contact-link"
                >
                  contacta con nosotros
                </Link>
              )}
              .
            </p>
          </motion.div>
        </section>

        {/* 8 — CTA final */}
        <section className="bg-zinc-50 border-t border-zinc-100">
          <div className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-20 py-16 sm:py-20 text-center">
            <motion.div {...fadeUp} transition={{ duration: 0.55 }}>
              <h2
                className="font-heading text-3xl sm:text-4xl font-bold tracking-tight text-zinc-950 leading-tight"
                data-testid="security-final-heading"
              >
                Tu información. Tus máquinas. Tu empresa.
              </h2>
              <p className="text-lg text-zinc-600 leading-relaxed mt-4 max-w-xl mx-auto">
                Asset Passport centraliza el conocimiento de tus activos sin convertirlo en
                información pública.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-3 mt-8">
                <Link
                  to="/solicitar-acceso"
                  data-testid="security-cta-primary"
                  className="inline-flex items-center justify-center h-12 px-8 rounded-full bg-zinc-900 text-white text-sm font-medium hover:bg-zinc-800 active:scale-[0.98] transition-[background-color,transform] duration-200"
                >
                  Probar Asset Passport
                </Link>
                <Link
                  to="/privacy"
                  data-testid="security-cta-privacy"
                  className="inline-flex items-center justify-center h-12 px-8 rounded-full border border-zinc-300 bg-white text-sm font-medium text-zinc-900 hover:border-zinc-400 active:scale-[0.98] transition-[border-color,transform] duration-200"
                >
                  Ver política de privacidad
                </Link>
              </div>
            </motion.div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
